import { useEffect, useState } from 'react'
import { useAuth } from '@/shared/hooks/useAuth'
import { config } from '@/config/env'
import type { NotificationModule } from '@/services/notificationsService'
import { moduloDelUsuario } from '@/shared/lib/moduloDelUsuario'

/**
 * El módulo en el que el usuario está trabajando, según su perfil.
 *
 * Hace falta porque hay pantallas compartidas —el dashboard, sobre todo— cuya
 * ruta es la misma para los tres módulos: ahí lo único que dice en cuál estás
 * es el módulo activo del usuario.
 *
 * El perfil guarda el id del módulo, no su clave, así que hay que cruzarlo con
 * `/users/me/modules`. Ese cruce se recuerda entre montajes: los ids de los
 * módulos no cambian, y la campanita se monta en cada pantalla.
 */

/** id → clave. Se llena una vez y sobrevive a los montajes de la campanita. */
let clavePorId: Record<string, string> | null = null
let consultaEnCurso: Promise<Record<string, string>> | null = null

async function claves(): Promise<Record<string, string>> {
  if (clavePorId) return clavePorId
  if (consultaEnCurso) return consultaEnCurso

  consultaEnCurso = (async () => {
    const token = localStorage.getItem('access_token')
    if (!token) return {}

    try {
      const res = await fetch(`${config.apiBaseUrl}/users/me/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return {}

      const data = await res.json()
      const modulos: { id?: string; key?: string }[] = Array.isArray(data?.modules)
        ? data.modules
        : []

      const mapa: Record<string, string> = {}
      for (const m of modulos) if (m.id && m.key) mapa[m.id] = m.key
      clavePorId = mapa
      return mapa
    } catch {
      // Sin esto la campanita se comporta como antes, no se rompe.
      return {}
    } finally {
      consultaEnCurso = null
    }
  })()

  return consultaEnCurso
}

export const useModuloActivo = (): NotificationModule => {
  const { user } = useAuth()
  const moduleId = user?.currentModuleId ?? null
  const [clave, setClave] = useState<string | null>(null)

  useEffect(() => {
    if (!moduleId) {
      setClave(null)
      return
    }

    let vigente = true
    claves().then((mapa) => {
      if (!vigente) return
      setClave(mapa[moduleId] ?? null)
    })

    return () => {
      vigente = false
    }
  }, [moduleId])

  // Cambiar de módulo desde el selector no recarga la página: sin esto, la
  // campanita seguiría filtrando por el módulo anterior hasta el próximo
  // refresh.
  useEffect(() => {
    const alCambiar = () => {
      clavePorId = null
      setClave(null)
    }
    window.addEventListener('module:changed', alCambiar)
    return () => window.removeEventListener('module:changed', alCambiar)
  }, [])

  // La misma regla con la que el dashboard elige qué panel pintar.
  return moduloDelUsuario({
    claveDelModuloActivo: clave,
    esMaster: user?.rbac?.isMaster,
    permisos: user?.rbac?.effectivePermissions,
  })
}
